import { Camera, Warning } from '@phosphor-icons/react';
import { textoSobre } from '@/api/formaleta';

// Piezas visuales compartidas por la pantalla de formaleta.
//
// La que manda acá es la REGLA DE INTERFAZ del expediente: «un dato que no
// existe se muestra EN ROJO como "sin registrar", nunca vacío ni en blanco.
// Vacío se lee como "no hay nada"; rotulado se lee como "falta cargarlo", que
// es la verdad y además lo vuelve una tarea visible».
//
// No es cosmética: hoy `IDEstado` es NULL en los 509 renglones, `AzureBlobLink`
// también en los 509, `Sector` y `TgDisponible` nunca se usaron y
// `FormMovimiento` tiene 0 filas. Si todo eso se pintara vacío, la pantalla
// diría que el inventario está sano.

/**
 * El rótulo rojo. `que` es el nombre del dato que falta, en el idioma de la
 * cuadrilla («sin foto», «sin categoría», «sin lavado registrado»).
 */
export function SinRegistrar({ que, detalle }: { que: string; detalle?: string }) {
  return (
    <span
      title={detalle}
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-red-200"
    >
      <Warning size={11} weight="bold" />
      {que}
    </span>
  );
}

/**
 * La pastilla de categoría o de estado. El hex viene de la BASE
 * (`FormCategoria.ColorHEXCat` / `FormEstadoPieza.ColorHEX`), nunca del CSS, y
 * el color del texto se calcula por luminancia porque los diez colores de
 * categoría no están validados para contraste.
 */
export function Pastilla({ texto, colorHex, titulo, grande }: {
  texto: string;
  colorHex: string | null;
  titulo?: string;
  grande?: boolean;
}) {
  const fondo = colorHex || '#D9D9D9';
  return (
    <span
      title={titulo || texto}
      style={{ backgroundColor: fondo, color: textoSobre(fondo) }}
      className={`inline-flex shrink-0 items-center rounded font-bold uppercase tracking-wide ${
        grande ? 'px-2 py-1 text-xs' : 'px-1.5 py-0.5 text-[10px]'
      }`}
    >
      {texto}
    </span>
  );
}

/**
 * La foto, que es lo PRIMERO de la tarjeta de campo:
 * `CR-105X50.4SAHIFWTSPARA29054 WALLS` no significa nada para un operario
 * nuevo. Cuando no hay, el hueco se rotula en rojo — y hoy no hay ninguna en
 * los 509 renglones, así que este estado es el normal al abrir la pantalla.
 */
export function Foto({ url, alt, lado = 72 }: { url: string | null; alt: string; lado?: number }) {
  return (
    <div
      style={{ width: lado, height: lado }}
      className="flex shrink-0 items-center justify-center overflow-hidden rounded border border-brand-gray-200 bg-brand-gray-100"
    >
      {url
        ? <img src={url} alt={alt} loading="lazy" className="h-full w-full object-cover" />
        : (
          <span className="flex flex-col items-center gap-0.5 text-brand-red-200">
            <Camera size={lado > 56 ? 20 : 14} weight="bold" />
            <span className="text-[9px] font-semibold leading-none">sin foto</span>
          </span>
        )}
    </div>
  );
}

/** El aviso de bloque: lo que no se puede hacer todavía y POR QUÉ. */
export function Aviso({ titulo, detalle, pista, tono = 'neutro' }: {
  titulo: string;
  detalle?: string | null;
  pista?: string;
  tono?: 'neutro' | 'alerta';
}) {
  const alerta = tono === 'alerta';
  return (
    <div className={`rounded-lg border p-4 ${
      alerta ? 'border-brand-red-200/40 bg-brand-red-200/5' : 'border-brand-gray-200 bg-white'
    }`}>
      <p className={`flex items-center gap-1.5 text-sm font-semibold ${
        alerta ? 'text-brand-red-200' : 'text-brand-gray-500'
      }`}>
        {alerta && <Warning size={15} weight="bold" />}
        {titulo}
      </p>
      {detalle && <p className="mt-1 text-xs text-brand-gray-400">{detalle}</p>}
      {pista && <p className="mt-2 text-xs text-brand-gray-400">{pista}</p>}
    </div>
  );
}

/**
 * «Inventariadas vs declaradas», el par de cifras que NO se debe colapsar en
 * una. Son dos hechos distintos: cuántas dice la compra que hay y cuántas se
 * han identificado una por una. La diferencia ES la deuda de inventariado que
 * describió Daniel («hay muchas que se han fabricado y nunca se han
 * inventariado»), así que la pantalla muestra las dos y la brecha.
 */
export function Conteo({ inventariadas, declaradas, habilitado }: {
  inventariadas: number;
  declaradas: number;
  habilitado: boolean;
}) {
  const faltan = Math.max(0, declaradas - inventariadas);
  return (
    <div className="shrink-0 text-right">
      <p className="font-mono text-lg font-bold leading-none text-brand-gray-500">
        {habilitado ? inventariadas : '—'}
        <span className="text-xs font-normal text-brand-gray-400"> / {declaradas}</span>
      </p>
      <p className="mt-0.5 text-[10px] leading-none text-brand-gray-400">
        {habilitado ? 'inventariadas' : 'declaradas'}
      </p>
      {habilitado && faltan > 0 && (
        <p className="mt-1 text-[10px] font-semibold leading-none text-brand-red-200">
          faltan {faltan}
        </p>
      )}
    </div>
  );
}

/** Chip de filtro con contador. Target grande: se toca con guante. */
export function Chip({ texto, conteo, activo, colorHex, onClick }: {
  texto: string;
  conteo?: number;
  activo: boolean;
  colorHex?: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${
        activo
          ? 'border-brand-gray-500 bg-brand-gray-500 text-white'
          : 'border-brand-gray-200 bg-white text-brand-gray-500 hover:border-brand-gray-300'
      }`}
    >
      {colorHex && (
        <span
          style={{ backgroundColor: colorHex }}
          className="h-2.5 w-2.5 shrink-0 rounded-full"
        />
      )}
      {texto}
      {conteo !== undefined && (
        <span className={activo ? 'text-white/70' : 'text-brand-gray-400'}>{conteo}</span>
      )}
    </button>
  );
}

/** `2026-10-22T…` → `22 oct 2026`, o el rótulo rojo si no hay dato. */
export function Fecha({ valor, que }: { valor: string | null; que: string }) {
  if (!valor) return <SinRegistrar que={que} />;
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return <SinRegistrar que={que} />;
  return (
    <span className="text-[11px] text-brand-gray-400">
      {d.toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' })}
    </span>
  );
}
